import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import { rotatedVisualAabbOfShape } from '@/lib/shapes/shape-coordinates'

/**
 * The bounds smart guides align and snap against: the shape's rotated **visual** AABB — the same
 * computed box selection chrome hugs (`selectionChromeFrameOfShape`) and the left panel shows as
 * X/Y/W/H (`rotatedVisualAabbOfShape`). `position` lets a caller ask "what would this shape's bounds
 * be if it were at (x, y)" without mutating it — used mid-drag, before the move is committed.
 *
 * This used to go through `canvasAabbOfRotatedPathBounds` fed by a DOM-measured `getBBox()` per
 * shape (`ShapeBounds`), which had a real bug: at ~0° rotation it discarded the measured bounds
 * entirely and substituted the full geometric placement frame, so an inset shape's (triangle, star)
 * guides silently jumped to its generator box's edges — not its visible outline — for the
 * overwhelmingly common unrotated case. `rotatedVisualAabbOfShape` is computed purely from the
 * shape's own kind configuration (never measured), so it has no such rotation-conditional branch and
 * matches what the shape actually draws at every angle, including 0°.
 */
export function objectBoundsOfShapeAtPosition(
  shape: ShapeData,
  position?: { x: number; y: number }
): ObjectBounds {
  const atPosition = position ? { ...shape, x: position.x, y: position.y } : shape

  return rotatedVisualAabbOfShape(atPosition)
}

export function unionObjectBounds(bounds: ObjectBounds[]): ObjectBounds | null {
  if (bounds.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const bound of bounds) {
    minX = Math.min(minX, bound.x)
    minY = Math.min(minY, bound.y)
    maxX = Math.max(maxX, bound.x + bound.width)
    maxY = Math.max(maxY, bound.y + bound.height)
  }

  return {
    x: minX,
    y: minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  }
}
